import { UnauthorizedError, handleError } from "../errors";
import { Result } from "./types";
import { getTenant } from "../utils/tenant";
import { browserStore } from "../utils/storageKeys";

const fetchAuthToken = async (idToken: string, tenant: string): Promise<Result<string>> => {
  try {
    const baseApiUrl = process.env.API_URL || "https://api.ediphi.com";
    const apiUrl = `${baseApiUrl}/api/projects`;

    const apiResponse = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${idToken}`,
        tenant: tenant || "",
      },
    });

    if (!apiResponse.ok) {
      throw new UnauthorizedError("API request failed", { status: apiResponse.status });
    }

    const xAuthToken = apiResponse.headers.get("x-auth");
    if (!xAuthToken) {
      throw new UnauthorizedError("x-auth header not found in the response.");
    }

    return { success: true, data: xAuthToken };
  } catch (error) {
    const err = handleError(error, { context: "fetchAuthToken" });
    return { success: false, error: err };
  }
};

/**
 * Revokes the caller's Ediphi session tokens server-side via POST /api/auth/logout.
 *
 * The credential that carries a session is the first-party Ediphi JWT stored as `xAuthToken`, not
 * the Keycloak token. It lives ~2 weeks, is never rotated, and is only killed early by a server-side
 * revoke. Clearing it from localStorage alone leaves a working credential behind, so logout must call
 * this before dropping the local copy (PLAT-1846 fixed this for the web app; DO-730 for the add-in).
 *
 * This calls fetch directly rather than routing through the shared api client so a revoke response
 * does not trip the global session-expired handler mid-logout — a 401 here just means the token was
 * already revoked (e.g. a web logout beat us to it), which is a success from our point of view.
 */
const revokeSessionTokens = async (): Promise<Result<void>> => {
  try {
    const baseApiUrl = process.env.API_URL || "https://api.ediphi.com";
    const xAuthToken = browserStore.get("xAuthToken");
    const tenant = getTenant();

    // Nothing stored means there is no server-side session to revoke — treat as success so logout
    // proceeds to tear down the local/SSO state as usual.
    if (!xAuthToken || !tenant) {
      return { success: true };
    }

    const apiResponse = await fetch(`${baseApiUrl}/api/auth/logout`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${xAuthToken}`,
        tenant,
      },
    });

    // A 401 means the token was already revoked — the server-side goal is met, so don't fail.
    if (!apiResponse.ok && apiResponse.status !== 401) {
      const error = new Error("Failed to revoke session tokens") as Error & { statusCode: number };
      error.statusCode = apiResponse.status;
      throw error;
    }

    return { success: true };
  } catch (error) {
    const err = handleError(error, { context: "revokeSessionTokens" });
    return { success: false, error: err };
  }
};

export { fetchAuthToken, revokeSessionTokens };
